import React, { useState, useEffect } from "react";
import { API_IP } from "../../env";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";

function ProviderDetailForm({
  show,
  onHide,
  providerID,
  providerName,
  setRefreshProvider,
  refreshProvider,
}) {
  const [providerData, setProviderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    const reqConfig = {
      url: url,
      method: method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: data,
    };
    const returnData = await axios(reqConfig);
    return returnData;
  }

  async function getTheProviderData() {
    if (!providerID) return;
    setIsLoading(true);
    try {
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/${providerID}/detail`,
        "get",
        []
      );
      setProviderData(data);
      setEditData(data);
    } catch (err) {
      console.error("Failed to fetch provider details", err);
      onHide();
    }
    setIsLoading(false);
  }

  async function UpdateVerification(verification) {
    setIsLoading(true);
    const options = verification ? "VERIFY" : "UNVERIFY";
    const answer = window.confirm(
      `Are you sure you want to ${options} this provider?`
    );
    if (answer) {
      await axiosReqConfig(`${API_IP}/api/admin/verification`, "patch", {
        provider_id: providerID,
        verification: verification,
      });
      setRefreshProvider((value) => !value);
      onHide();
    }
    setIsLoading(false);
  }

  async function handleSaveChanges() {
    setIsLoading(true);
    try {
      await axiosReqConfig(
        `${API_IP}/api/admin/providers/${providerID}/detail`,
        "patch",
        editData
      );
      setProviderData(editData);
      setIsEditing(false);
      setRefreshProvider((value) => !value);
    } catch (err) {
      console.error("Failed to update provider", err);
      alert("Failed to update provider details.");
    }
    setIsLoading(false);
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (show) {
      setIsEditing(false);
      getTheProviderData();
    }
  }, [show, providerID]);

  const renderLoading = () => (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: "200px" }}
    >
      <Spinner animation="border" variant="primary" />
    </div>
  );

  const renderDetails = () => (
    <Container fluid className="px-0">
      <div className="p-3 mb-3 bg-light border rounded">
        <h6 className="text-primary fw-bold mb-3">General Information</h6>
        <Row>
          <Col md={6} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Provider Name</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="provider_name"
                value={editData.provider_name || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.provider_name || providerName}</div>
            )}
          </Col>
          <Col md={6} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Title</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="title"
                value={editData.title || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.title || "N/A"}</div>
            )}
          </Col>
          <Col md={12}>
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Experience</span>
            {isEditing ? (
              <Form.Control
                as="textarea"
                rows={2}
                name="experience"
                value={editData.experience || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.experience || "N/A"}</div>
            )}
          </Col>
        </Row>
      </div>

      <div className="p-3 mb-3 bg-light border rounded">
        <h6 className="text-primary fw-bold mb-3">Contact & Location</h6>
        <Row>
          <Col md={6} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Email</span>
            {isEditing ? (
              <Form.Control
                type="email"
                name="email"
                value={editData.email || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.email}</div>
            )}
          </Col>
          <Col md={6} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Phone Number</span>
            {isEditing ? (
              <Form.Control
                type="number"
                name="phone_number"
                value={editData.phone_number || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.phone_number}</div>
            )}
          </Col>
          <Col md={12} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Address</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="address"
                value={editData.address || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.address}</div>
            )}
          </Col>
          <Col md={4} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>City</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="city"
                value={editData.city || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.city}</div>
            )}
          </Col>
          <Col md={4} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Province</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="province"
                value={editData.province || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.province}</div>
            )}
          </Col>
          <Col md={4} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Country</span>
            {isEditing ? (
              <Form.Control
                type="text"
                name="country"
                value={editData.country || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.country}</div>
            )}
          </Col>
        </Row>
      </div>

      <div className="p-3 mb-3 bg-light border rounded">
        <h6 className="text-primary fw-bold mb-3">Operating Hours</h6>
        <Row>
          <Col md={6}>
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Open Hour</span>
            {isEditing ? (
              <Form.Control
                type="number"
                name="opening_hour"
                value={editData.opening_hour || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">
                {providerData.opening_hour}
                {providerData.opening_hour < 12 ? " AM" : " PM"}
              </div>
            )}
          </Col>
          <Col md={6}>
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Close Hour</span>
            {isEditing ? (
              <Form.Control
                type="number"
                name="closing_hour"
                value={editData.closing_hour || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">
                {providerData.closing_hour}
                {providerData.closing_hour >= 12 ? " PM" : " AM"}
              </div>
            )}
          </Col>
        </Row>
      </div>

      <div className="p-3 bg-light border rounded">
        <h6 className="text-primary fw-bold mb-3">Professional Bio</h6>
        <Row>
          <Col md={12} className="mb-3">
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Credentials</span>
            {isEditing ? (
              <Form.Control
                as="textarea"
                rows={2}
                name="credentials"
                value={editData.credentials || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.credentials || "N/A"}</div>
            )}
          </Col>
          <Col md={12}>
            <span className="text-muted d-block mb-1" style={{ fontSize: "0.85rem" }}>Self Description</span>
            {isEditing ? (
              <Form.Control
                as="textarea"
                rows={3}
                name="self_description"
                value={editData.self_description || ""}
                onChange={handleChange}
                size="sm"
              />
            ) : (
              <div className="fw-semibold">{providerData.self_description || "N/A"}</div>
            )}
          </Col>
        </Row>
      </div>
    </Container>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
      <Modal.Header
        closeButton
        className={
          providerData?.verification
            ? "bg-success text-white"
            : "bg-warning text-dark"
        }
      >
        <Modal.Title className="fw-bold">
          {isEditing ? "Edit Details for:" : "Details for:"} {providerName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {isLoading || !providerData ? renderLoading() : renderDetails()}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {isLoading || !providerData ? null : (
          <>
            {isEditing ? (
              <Button variant="success" onClick={handleSaveChanges}>
                Save Changes
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                Edit Details
              </Button>
            )}
            {!isEditing && (
              providerData.verification ? (
                <Button
                  variant="danger"
                  onClick={() => {
                    UpdateVerification(false);
                  }}
                >
                  Unverify
                </Button>
              ) : (
                <Button
                  variant="success"
                  onClick={() => {
                    UpdateVerification(true);
                  }}
                >
                  Verify
                </Button>
              )
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ProviderDetailForm;
